import { useEffect, useMemo, useState, useRef } from 'react'
import { Box, IconButton, ListSubheader, MenuItem } from '@mui/material'
import { useTranslator, useDebounce } from '@/hooks'
import { getCountryPhoneOptions } from '@data/countries'
import CustomTextField from '@/@core/components/mui/TextField'
import { useFormContext } from 'react-hook-form'

interface PhoneCodeSelectionProps {
  value?: string | string[] | number | number[]
  multiple?: boolean
  onChange?: (value: string, country: any) => void
  disabled?: boolean
  required?: boolean
  label?: string
  placeholder?: string
  className?: string
  fullWidth?: boolean
  onClear?: () => void
  error?: boolean
  helperText?: string
  errorMessage?: string
}

const ITEMS_PER_PAGE = 10

const PhoneCodeSelection = ({
  value = '',
  multiple = false,
  onChange,
  onClear,
  disabled = false,
  label = '',
  placeholder = '',
  error = false,
  errorMessage = ''
}: PhoneCodeSelectionProps) => {
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const { __ } = useTranslator()
  const searchInputRef = useRef<HTMLInputElement>(null)
  const phoneOptions = getCountryPhoneOptions()
  const debouncedSearch = useDebounce(search, 100)

  const { clearErrors  } = useFormContext()

  const filteredOptions = useMemo(() => {
    if (!debouncedSearch) return phoneOptions

    return phoneOptions.filter(
      option =>
        option.label.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
        option.value.toString().includes(debouncedSearch)
    )
  }, [debouncedSearch])

  const paginatedOptions = useMemo(() => {
    const startIndex = (page - 1) * ITEMS_PER_PAGE

    return filteredOptions.slice(startIndex, startIndex + ITEMS_PER_PAGE)
  }, [filteredOptions, page])

  useEffect(() => {
    if (filteredOptions) {
      setTimeout(() => searchInputRef.current?.focus())
    }
  }, [filteredOptions])

  useEffect(() => {
    setPage(1)
  }, [debouncedSearch])

  return (
    <CustomTextField
      select
      value={value || (multiple ? [] : '')}
      label={label}
      onChange={e => {
        clearErrors('phone')
        onChange?.(
          e.target.value,
          phoneOptions.find(option => option.value === e.target.value)
        )
      }}
      fullWidth
      error={error}
      helperText={errorMessage}
      disabled={disabled}
      SelectProps={{
        multiple,
        displayEmpty: true,
        renderValue: (selected: any) => {
          if (!selected || (Array.isArray(selected) && selected.length === 0)) {
            return <span style={{ color: '#aaa' }}>{placeholder || label || __('Select...')}</span>
          }

          if (multiple && Array.isArray(selected)) {
            const selectedLabels = filteredOptions
              .filter(option => selected.map(String).includes(String(option.value)))
              .map(option => option.label)
              .join(', ')

            return <>{selectedLabels}</>
          }

          const found = filteredOptions.find(option => String(option.value) === String(selected))

          return <>{found ? found.value : selected}</>
        }
      }}
      slotProps={{
        input: {
          endAdornment:
            onClear && value && ((multiple && Array.isArray(value) && value.length > 0) || (!multiple && value)) ? (
              <Box mr={5}>
                <IconButton
                  size='small'
                  onClick={() => {
                    onClear?.()
                  }}
                  aria-label='clear selection'
                  tabIndex={-1}
                >
                  <i className='tabler-x text-[16px] text-textPrimary'></i>
                </IconButton>
              </Box>
            ) : null
        }

      }}
      sx={{
        '& .MuiSelect-select': {
          textAlign: 'left',
          '&:not(textarea).MuiInputBase-inputSizeSmall': {
            padding: '0 !important'
          }
        },
        '& .MuiInputBase-input': {
          '&:not(textarea).MuiInputBase-inputSizeSmall': {
            padding: '7.25px 0px 7.25px 10px !important',
            fontSize: '15px',
            lineHeight: '1.5'
          }
        },
        '& .MuiFormLabel-root': {
          fontSize: '15px',
          fontWeight: '400',
          color: '#2f2b3de6'
        },
        '& .MuiFormHelperText-root': {
          whiteSpace: 'nowrap',
        },
        '& .MuiInputLabel-root:not(.Mui-error).MuiFormLabel-colorPrimary.Mui-focused': {
          color: '#2f2b3de6 !important'
        },

        '& .MuiInputBase-root.Mui-focused': {
          boxShadow: 'none'
        },
        '& .MuiFormLabel-asterisk': {
          color: '#FF4C51'
        }
      }}
    >
      <ListSubheader>
        <CustomTextField
          fullWidth
          placeholder={__('Search Country/Code')}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={e => e.stopPropagation()}
          inputRef={searchInputRef}
        />
      </ListSubheader>
      {paginatedOptions.map(option => (
        <MenuItem onKeyDown={e => e.stopPropagation()} key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}

    </CustomTextField>
  )
}

export default PhoneCodeSelection
